/**
 * 2026-04-25 Phase 32-C.6 — 장비 시각 보상 (오라).
 *
 * 제련 단계·각인 tier 를 시각적 만족감으로 번역. 성능 부담 최소 (CSS transform·opacity 만).
 *
 * 렌더 원칙:
 *   - enhancement < 4: 없음
 *   - enhancement +4~+6 또는 SUCCESS tier: 은은한 은/동빛 radial glow
 *   - enhancement +7~+9 또는 GREAT tier: 밝은 황금 radial glow + pulse
 *   - enhancement +10: 무지개 링 + 떠오르는 별 3개
 *
 * 장착 슬롯 / 상세 카드 / 인벤토리 슬롯에서 모두 재사용.
 */
import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";

export interface EquipmentAuraProps {
    /** 0~10. */
    enhancement: number;
    /** "SUCCESS" | "GREAT" | null/undefined */
    engraveTier?: "SUCCESS" | "GREAT" | null;
    /** 초월 대성공 기록 있음 (SSSS). 향후 서버 저장 시 true. */
    transcendent?: boolean;
    /** 컴팩트 모드 — 인벤토리 카드처럼 작은 공간에 배치. */
    compact?: boolean;
    /** 장착 슬롯처럼 목록보다 한 단계 더 존재감 있게. */
    slotMode?: boolean;
}

export function resolveEquipmentAuraLevel(
    enhancement: number,
    engraveTier?: "SUCCESS" | "GREAT" | null,
): number {
    const enhLevel = enhancement >= 10 ? 4
        : enhancement >= 7 ? 3
        : enhancement >= 4 ? 2
        : 0;
    const tierLevel = engraveTier === "GREAT" ? 3
        : engraveTier === "SUCCESS" ? 2
        : 0;
    return Math.max(enhLevel, tierLevel);
}

export function resolveEquipmentAuraColor(
    enhancement: number,
    engraveTier?: "SUCCESS" | "GREAT" | null,
    transcendent?: boolean,
): string {
    return enhancement >= 10 || transcendent
        ? tokens.color.brass
        : (engraveTier === "GREAT" || enhancement >= 7)
            ? tokens.color.brass
            : (engraveTier === "SUCCESS" || enhancement >= 4)
                ? tokens.color.moss
                : tokens.color.parchmentDim;
}

/**
 * 카드 뒤에 absolute 로 깔리는 오라 레이어. 부모는 position:relative 여야 함.
 * pointer-events: none 이라 위 콘텐츠는 정상 상호작용.
 */
export function EquipmentAura({
    enhancement, engraveTier, transcendent, compact, slotMode,
}: EquipmentAuraProps) {
    const level = resolveEquipmentAuraLevel(enhancement, engraveTier);

    if (level === 0 && !transcendent) return null;

    const color = resolveEquipmentAuraColor(enhancement, engraveTier, transcendent);

    const glowRadius = compact ? (8 + level * 4) : slotMode ? (14 + level * 7) : (20 + level * 12);
    const glowOpacity = compact ? (0.11 + level * 0.05) : slotMode ? (0.16 + level * 0.06) : (0.15 + level * 0.08);
    const pulse = level >= 3;
    const showSparkles = level >= 4 && !compact;
    const showRainbow = (enhancement >= 10 || transcendent) && !compact;
    const showCompactRing = compact && (enhancement >= 10 || transcendent);
    const showSlotHalo = slotMode && level >= 2;

    return (
        <div
            aria-hidden
            style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                borderRadius: "inherit",
                overflow: compact ? "hidden" : "visible",
                zIndex: 0,
            }}
        >
            {/* 메인 glow */}
            <motion.div
                animate={pulse ? { opacity: [glowOpacity * 0.7, glowOpacity, glowOpacity * 0.7] } : { opacity: glowOpacity }}
                transition={pulse ? { duration: 2.5, repeat: Infinity, ease: "easeInOut" } : undefined}
                style={{
                    position: "absolute",
                    inset: -glowRadius,
                    borderRadius: "inherit",
                    background: showRainbow
                        ? `conic-gradient(from 0deg, ${tokens.color.brass}, ${tokens.color.moss}, ${tokens.color.ember}, ${tokens.color.brass})`
                        : `radial-gradient(circle, ${color}88 0%, ${color}22 40%, transparent 75%)`,
                    filter: showRainbow ? `blur(${glowRadius * 0.6}px)` : `blur(${glowRadius * 0.35}px)`,
                }}
            />

            {showCompactRing && (
                <motion.div
                    animate={{ opacity: [0.35, 0.55, 0.35] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                        position: "absolute",
                        inset: 1,
                        borderRadius: "inherit",
                        border: `1px solid ${tokens.color.brass}66`,
                        boxShadow: `0 0 10px ${tokens.color.brass}22 inset`,
                    }}
                />
            )}

            {showSlotHalo && (
                <motion.div
                    animate={pulse ? { opacity: [0.35, 0.6, 0.35] } : { opacity: 0.42 }}
                    transition={pulse ? { duration: 3.2, repeat: Infinity, ease: "easeInOut" } : undefined}
                    style={{
                        position: "absolute",
                        inset: 3,
                        borderRadius: "inherit",
                        border: `1px solid ${color}55`,
                        boxShadow: `inset 0 0 18px ${color}22, 0 0 16px ${color}22`,
                    }}
                />
            )}

            {/* 무지개 링 회전 (lv4 전용) */}
            {showRainbow && (
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
                    style={{
                        position: "absolute",
                        inset: -4,
                        borderRadius: "inherit",
                        background: `conic-gradient(from 0deg, ${tokens.color.brass}66, transparent 25%, ${tokens.color.moss}66 50%, transparent 75%, ${tokens.color.brass}66)`,
                        mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 2px))",
                        WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 2px))",
                    }}
                />
            )}

            {/* 부유 별 3개 (+10 또는 초월) */}
            {showSparkles && (
                <>
                    {[0, 1, 2].map(i => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 10, x: (i - 1) * 30 }}
                            animate={{ opacity: [0, 0.9, 0], y: [-30, -60, -90] }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                delay: i * 1.3,
                                ease: "easeOut",
                            }}
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                width: 6,
                                height: 6,
                                marginLeft: -3,
                                marginTop: -3,
                                borderRadius: "50%",
                                background: tokens.color.brass,
                                boxShadow: `0 0 10px ${tokens.color.brass}, 0 0 20px ${tokens.color.brass}88`,
                            }}
                        />
                    ))}
                </>
            )}
        </div>
    );
}
